import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { StoreSlice } from "../types/StoreSlice";
import IRailFreightDetails from "../types/IRailFreightDetails";
import {
  FreightTruck,
  IDestinationExtension,
  IOriginExtension,
  IQuoteExtension,
} from "../types/IQuotes";
import IModes from "../types/IModes";

export type LabelType = "city" | "location" | "prices";

export type TariffRowStatus =
  | "dash"
  | "checking"
  | "found"
  | "not found"
  | "try again"
  | "check";

export type PendingTariffPollRequest = {
  rowKey: string;
  quote: IQuoteExtension;
  destination: IDestinationExtension;
  // Experimental: lets a poll run without surfacing checking/countdown UI. Useful for
  // Missing Rail followup items dedup-hidden from the table; remove if it proves unhelpful.
  silent?: boolean;
};

export type MoveToOriginTableFn = (params: {
  quote: IQuoteExtension;
  foundQuote: IQuoteExtension;
}) => void;

export interface ICompetitiveAnalysisSlice {
  isLoading: boolean;
  hadError: boolean;

  routeValidationOpen: boolean;
  troubleshootQuoteOpen: boolean;
  setTroubleshootQuoteOpen: (open: boolean) => void;
  // Set when Occupants/Logistics actually persist a change during a troubleshoot session.
  insightsPanelSaved: boolean;
  setInsightsPanelSaved: (saved: boolean) => void;
  displayAddProductToDestinationPanel: boolean;
  setDisplayAddProductToDestinationPanel: (open: boolean) => void;
  displayAddProductToOriginPanel: boolean;
  setDisplayAddProductToOriginPanel: (open: boolean) => void;
  originToTroubleshoot: { origin: IOriginExtension; isKoch?: boolean };
  setOriginToTroubleshoot: (origin: IOriginExtension, isKoch?: boolean) => void;
  destinationToTroubleshoot: IDestinationExtension;
  setDestinationToTroubleshoot: (destination: IDestinationExtension) => void;
  expandedDestination?: {
    id: string;
    name: string;
    expandTruckWithNoFreight: boolean;
    expandRailWithNoFreight: boolean;
    expandSurroundingLocations: boolean;
  };
  setExpandedDestination: (
    destination?: {
      id: string;
      name: string;
    },
    expandSettings?: {
      expandTruckWithNoFreight?: boolean;
      expandRailWithNoFreight?: boolean;
      expandSurroundingLocations?: boolean;
    },
  ) => void;
  closeRailFreightDetailsPanel: boolean;
  setRouteValidationOpen: (open: boolean) => void;
  resetRouteValidationState: number;
  triggerReset: () => void;
  originFacilityName: string;
  setOriginFacilityName: (name: string) => void;
  selectYourOfferFreight: boolean;
  setSelectYourOfferFreight: (selected: boolean) => void;
  displayRailFreightDetailsPanel: boolean;
  setDisplayRailFreightDetailsPanel: (open: boolean) => void;
  displayTruckFreightDetailsPanel: boolean;
  setDisplayTruckFreightDetailsPanel: (open: boolean) => void;
  displayAdditionalCarriersPanel: boolean;
  setDisplayAdditionalCarriersPanel: (open: boolean) => void;
  displayInboundModeTogglePanel: boolean;
  setDisplayInboundModeTogglePanel: (open: boolean) => void;
  displayAddInboundRailPanel: boolean;
  setDisplayAddInboundRailPanel: (open: boolean) => void;
  displayTurnOffModePanel: boolean;
  setDisplayTurnOffModePanel: (open: boolean) => void;
  displayRailRateRequestPanel: boolean;
  setDisplayRailRateRequestPanel: (open: boolean) => void;
  exitRequestMultipleModeTrigger: number;
  triggerExitRequestMultipleMode: () => void;
  showRailRateRequestFailedPopup: boolean;
  setShowRailRateRequestFailedPopup: (open: boolean) => void;
  railRateRequestContext: {
    rows: {
      rowKey: string;
      quote: IQuoteExtension;
      silent?: boolean;
    }[];
    destination: IDestinationExtension;
  } | null;
  setRailRateRequestContext: (
    context: {
      rows: {
        rowKey: string;
        quote: IQuoteExtension;
        silent?: boolean;
      }[];
      destination: IDestinationExtension;
    } | null,
  ) => void;
  refetchQuotes: number;
  triggerRefetchQuotes: () => void;
  requestedRailRateRows: Set<string>;
  addRequestedRailRateRow: (rowKeys: string[]) => void;
  tariffStateByRowKey: Record<
    string,
    { status: TariffRowStatus; rate?: string; secondsRemaining?: number }
  >;
  setTariffStateForRow: (
    rowKey: string,
    entry: {
      status: TariffRowStatus;
      rate?: string;
      secondsRemaining?: number;
    },
  ) => void;
  pendingTariffPollRequests: PendingTariffPollRequest[];
  enqueueTariffPollRequests: (requests: PendingTariffPollRequest[]) => void;
  clearPendingTariffPollRequests: () => void;
  moveToOriginTableRegistry: Record<string, MoveToOriginTableFn>;
  registerMoveToOriginTable: (
    destinationId: string,
    fn: MoveToOriginTableFn,
  ) => void;
  unregisterMoveToOriginTable: (destinationId: string) => void;
  railTeamNotifiedRows: Set<string>;
  addRailTeamNotifiedRow: (rowKey: string) => void;
  surroundingQuoteOpen: boolean;
  setSurroundingQuoteOpen: (open: boolean) => void;
  destinationQuoteOpen: boolean;
  setDestinationQuoteOpen: (open: boolean) => void;
  closeSurroundingQuote: number;
  triggerCloseSurroundingQuote: () => void;
  closeDestinationQuote: number;
  triggerCloseDestinationQuote: () => void;
  expandedDestId?: string;
  setExpandedDestId: (id?: string) => void;
  expandRailOriginsWithNoFreightTrigger: number;
  setExpandRailOriginsWithNoFreightTrigger: () => void;
  resetExpandRailOriginsWithNoFreight: () => void;
  expandTruckOriginsWithNoFreightTrigger: number;
  setExpandTruckOriginsWithNoFreightTrigger: () => void;
  resetExpandTruckOriginsWithNoFreight: () => void;
  modeToTurnOff?: string;
  setModeToTurnOff: (mode?: string) => void;
  additionalCarriers: string[];
  setAdditionalCarriers: (carriers: string[]) => void;
  inboundModes?: IModes;
  setInboundModes: (modes?: IModes) => void;
  anchorForTruckDetailsPanel: HTMLElement | null;
  setAnchorForTruckDetailsPanel: (anchor: HTMLElement | null) => void;
  anchorForAdditionalCarriersPanel: HTMLElement | null;
  setAnchorForAdditionalCarriersPanel: (anchor: HTMLElement | null) => void;
  anchorForInboundModesTogglePanel: HTMLElement | null;
  setAnchorForInboundModesTogglePanel: (anchor: HTMLElement | null) => void;
  setCloseRailFreightDetailsPanel: (close: boolean) => void;
  selectedRailDetailRecord?: IRailFreightDetails;
  selectedTruckDetailRecord?: FreightTruck;
  setSelectedRailDetailRecord: (record: IRailFreightDetails) => void;
  setSelectedTruckDetailRecord: (record: FreightTruck) => void;
  railFreightDetailContext: {
    quote: IQuoteExtension;
    destination: IDestinationExtension;
    rowKey: string;
  } | null;
  setRailFreightDetailContext: (
    context: {
      quote: IQuoteExtension;
      destination: IDestinationExtension;
      rowKey: string;
    } | null,
  ) => void;
  reloadAfterTroubleshoot: number;
  triggerReload: () => void;
  hiddenOrigins: {
    truck: Set<string>;
    rail: Set<string>;
  };
  toggleOriginVisibility: (
    originName: string,
    mode: "truck" | "rail",
    visible: boolean,
  ) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createCompetitiveAnalysisSlice: StoreSlice<ICompetitiveAnalysisSlice> = (
  set,
) => {
  const initialState = {
    records: [],
    isLoading: false,
    hadError: false,
    routeValidationOpen: false,
    troubleshootQuoteOpen: false,
    insightsPanelSaved: false,
    displayAddProductToDestinationPanel: false,
    displayAddProductToOriginPanel: false,
    displayRailFreightDetailsPanel: false,
    displayTruckFreightDetailsPanel: false,
    displayAdditionalCarriersPanel: false,
    displayInboundModeTogglePanel: false,
    displayAddInboundRailPanel: false,
    displayTurnOffModePanel: false,
    displayRailRateRequestPanel: false,
    exitRequestMultipleModeTrigger: 0,
    showRailRateRequestFailedPopup: false,
    railRateRequestContext: null,
    refetchQuotes: 0,
    requestedRailRateRows: new Set<string>(),
    railTeamNotifiedRows: new Set<string>(),
    tariffStateByRowKey: {},
    pendingTariffPollRequests: [],
    moveToOriginTableRegistry: {},
    railFreightDetailContext: null,
    surroundingQuoteOpen: false,
    destinationQuoteOpen: false,
    expandRailOriginsWithNoFreightTrigger: 0,
    expandTruckOriginsWithNoFreightTrigger: 0,
    anchorForTruckDetailsPanel: null,
    anchorForAdditionalCarriersPanel: null,
    anchorForInboundModesTogglePanel: null,
    closeRailFreightDetailsPanel: false,
    selectYourOfferFreight: false,
    selectedRailDetailRecord: undefined,
    resetRouteValidationState: 0,
    reloadAfterTroubleshoot: 0,
    closeSurroundingQuote: 0,
    closeDestinationQuote: 0,
    originFacilityName: "",
    hiddenOrigins: {
      truck: new Set<string>(),
      rail: new Set<string>(),
    },
    originToTroubleshoot: {
      origin: {
        id: "",
        name: "",
        coordinate: [0, 0] as [number, number],
        isCity: false,
      } as IOriginExtension,
      isKoch: undefined,
    },
    destinationToTroubleshoot: {
      id: "",
      name: "",
      city: null,
      stateOrProvince: null,
      coordinate: [0, 0],
      isCity: false,
    } as IDestinationExtension,
    additionalCarriers: [],
  };

  return {
    ...initialState,

    triggerReset: () =>
      set((state) => ({
        resetRouteValidationState: state.resetRouteValidationState + 1,
      })),

    setRouteValidationOpen: (open: boolean) => {
      set({ routeValidationOpen: open });
    },
    setTroubleshootQuoteOpen: (open: boolean) => {
      set({ troubleshootQuoteOpen: open });
    },
    setInsightsPanelSaved: (saved: boolean) => {
      set({ insightsPanelSaved: saved });
    },
    setDisplayAddProductToDestinationPanel: (open: boolean) => {
      set({ displayAddProductToDestinationPanel: open });
    },
    setDisplayAddProductToOriginPanel: (open: boolean) => {
      set({ displayAddProductToOriginPanel: open });
    },
    setOriginFacilityName: (name: string) => {
      set({ originFacilityName: name });
    },
    setDisplayRailFreightDetailsPanel: (open: boolean) => {
      set({ displayRailFreightDetailsPanel: open });
    },
    setDisplayTruckFreightDetailsPanel: (open: boolean) => {
      set({ displayTruckFreightDetailsPanel: open });
    },
    setDisplayAdditionalCarriersPanel: (open: boolean) => {
      set({ displayAdditionalCarriersPanel: open });
    },
    setDisplayInboundModeTogglePanel: (open: boolean) => {
      set({ displayInboundModeTogglePanel: open });
    },
    setDisplayAddInboundRailPanel: (open: boolean) => {
      set({ displayAddInboundRailPanel: open });
    },
    setDisplayTurnOffModePanel: (open: boolean) => {
      set({ displayTurnOffModePanel: open });
    },
    setDisplayRailRateRequestPanel: (open: boolean) => {
      set({ displayRailRateRequestPanel: open });
    },
    triggerExitRequestMultipleMode: () => {
      set((state) => ({
        exitRequestMultipleModeTrigger:
          state.exitRequestMultipleModeTrigger + 1,
      }));
    },
    setShowRailRateRequestFailedPopup: (open: boolean) => {
      set({ showRailRateRequestFailedPopup: open });
    },
    setRailRateRequestContext: (
      context: {
        rows: { rowKey: string; quote: IQuoteExtension }[];
        destination: IDestinationExtension;
      } | null,
    ) => {
      set({ railRateRequestContext: context });
    },
    triggerRefetchQuotes: () => {
      set((state) => ({
        refetchQuotes: state.refetchQuotes + 1,
        requestedRailRateRows: new Set<string>(),
        tariffStateByRowKey: Object.fromEntries(
          Object.entries(state.tariffStateByRowKey).filter(
            ([, v]) => v.status === "checking",
          ),
        ),
      }));
    },
    addRequestedRailRateRow: (rowKeys: string[]) => {
      set((state) => ({
        requestedRailRateRows: new Set([
          ...state.requestedRailRateRows,
          ...rowKeys,
        ]),
      }));
    },
    setTariffStateForRow: (rowKey, entry) => {
      set((state) => ({
        tariffStateByRowKey: { ...state.tariffStateByRowKey, [rowKey]: entry },
      }));
    },
    enqueueTariffPollRequests: (requests) => {
      set((state) => ({
        pendingTariffPollRequests: [
          ...state.pendingTariffPollRequests,
          ...requests,
        ],
      }));
    },
    clearPendingTariffPollRequests: () => {
      set({ pendingTariffPollRequests: [] });
    },
    registerMoveToOriginTable: (destinationId, fn) => {
      set((state) => ({
        moveToOriginTableRegistry: {
          ...state.moveToOriginTableRegistry,
          [destinationId]: fn,
        },
      }));
    },
    unregisterMoveToOriginTable: (destinationId) => {
      set((state) => {
        const next = { ...state.moveToOriginTableRegistry };
        delete next[destinationId];
        return { moveToOriginTableRegistry: next };
      });
    },
    addRailTeamNotifiedRow: (rowKey: string) => {
      if (!rowKey) {
        return;
      }

      set((state) => ({
        railTeamNotifiedRows: new Set([...state.railTeamNotifiedRows, rowKey]),
      }));
    },
    setSelectedRailDetailRecord: (record: IRailFreightDetails) => {
      set({ selectedRailDetailRecord: record });
    },
    setRailFreightDetailContext: (
      context: {
        quote: IQuoteExtension;
        destination: IDestinationExtension;
        rowKey: string;
      } | null,
    ) => {
      set({ railFreightDetailContext: context });
    },
    setSelectedTruckDetailRecord: (record: FreightTruck) => {
      set({ selectedTruckDetailRecord: record });
    },
    setAnchorForTruckDetailsPanel: (anchor: HTMLElement | null) => {
      set({ anchorForTruckDetailsPanel: anchor });
    },
    setAnchorForAdditionalCarriersPanel: (anchor: HTMLElement | null) => {
      set({ anchorForAdditionalCarriersPanel: anchor });
    },
    setAnchorForInboundModesTogglePanel: (anchor: HTMLElement | null) => {
      set({ anchorForInboundModesTogglePanel: anchor });
    },
    setSelectYourOfferFreight: (selected: boolean) => {
      set({ selectYourOfferFreight: selected });
    },
    setCloseRailFreightDetailsPanel: (close: boolean) => {
      set({ closeRailFreightDetailsPanel: close });
    },
    triggerReload: () =>
      set((state) => ({
        reloadAfterTroubleshoot: state.reloadAfterTroubleshoot + 1,
      })),

    toggleOriginVisibility: (
      originName: string,
      mode: "truck" | "rail",
      visible: boolean,
    ) => {
      set((state) => {
        const newHiddenOrigins = {
          ...state.hiddenOrigins,
          [mode]: new Set(state.hiddenOrigins[mode]),
        };

        if (visible) {
          newHiddenOrigins[mode].delete(originName);
        } else {
          newHiddenOrigins[mode].add(originName);
        }

        return { hiddenOrigins: newHiddenOrigins };
      });
    },
    setOriginToTroubleshoot: (origin: IOriginExtension, isKoch?: boolean) => {
      set({ originToTroubleshoot: { origin, isKoch } });
    },
    setDestinationToTroubleshoot: (destination: IDestinationExtension) => {
      set({ destinationToTroubleshoot: destination });
    },
    setExpandedDestination: (
      destination?: { id: string; name: string },
      expandSettings?: {
        expandTruckWithNoFreight?: boolean;
        expandRailWithNoFreight?: boolean;
        expandSurroundingLocations?: boolean;
      },
    ) => {
      console.log(
        "Setting expanded destination to",
        destination,
        expandSettings,
      );
      // set settings to false by default if not provided
      const settings = {
        expandTruckWithNoFreight:
          expandSettings?.expandTruckWithNoFreight ?? false,
        expandRailWithNoFreight:
          expandSettings?.expandRailWithNoFreight ?? false,
        expandSurroundingLocations:
          expandSettings?.expandSurroundingLocations ?? false,
      };
      const expandedDestination = destination
        ? { id: destination.id, name: destination.name, ...settings }
        : undefined;

      set({
        expandedDestination,
      });
    },
    setAdditionalCarriers: (carriers: string[]) => {
      set({ additionalCarriers: carriers });
    },
    setInboundModes: (modes?: IModes) => {
      set({ inboundModes: modes });
    },
    setModeToTurnOff: (mode?: string) => {
      set({ modeToTurnOff: mode });
    },
    setExpandRailOriginsWithNoFreightTrigger: () =>
      set((state) => ({
        expandRailOriginsWithNoFreightTrigger:
          state.expandRailOriginsWithNoFreightTrigger + 1,
      })),
    resetExpandRailOriginsWithNoFreight: () =>
      set({ expandRailOriginsWithNoFreightTrigger: 0 }),
    setExpandTruckOriginsWithNoFreightTrigger: () =>
      set((state) => ({
        expandTruckOriginsWithNoFreightTrigger:
          state.expandTruckOriginsWithNoFreightTrigger + 1,
      })),
    resetExpandTruckOriginsWithNoFreight: () =>
      set({ expandTruckOriginsWithNoFreightTrigger: 0 }),
    setSurroundingQuoteOpen: (open: boolean) => {
      set({ surroundingQuoteOpen: open });
    },
    setDestinationQuoteOpen: (open: boolean) => {
      set({ destinationQuoteOpen: open });
    },
    triggerCloseSurroundingQuote: () =>
      set((state) => ({
        closeSurroundingQuote: state.closeSurroundingQuote + 1,
      })),
    triggerCloseDestinationQuote: () =>
      set((state) => ({
        closeDestinationQuote: state.closeDestinationQuote + 1,
      })),
    setExpandedDestId: (id?: string) => {
      set({ expandedDestId: id });
    },
  };
};

export const useNitroCAStore = create(
  subscribeWithSelector<ICompetitiveAnalysisSlice>((set: any, get: any) => ({
    ...createCompetitiveAnalysisSlice(set, get),
  })),
);
